'use client'

import {
    AccountDocumentGroup,
    AccountDocuments,
    deleteAccountDocumentFile,
    getAccountDocuments,
    sendAccountDocument,
} from '@/lib/api/financial/payment-account-api'
import { useEffect, useRef, useState } from 'react'
import {
    MdCheckCircle,
    MdCloudUpload,
    MdDelete,
    MdErrorOutline,
    MdHourglassEmpty,
    MdInsertDriveFile,
    MdOpenInNew,
    MdRefresh,
    MdWarningAmber,
} from 'react-icons/md'
import { toast } from 'react-toastify'

// Status raw do provider -> rótulo + estilo do badge.
const DOC_STATUS: Record<
    string,
    { label: string; badge: string; icon: any }
> = {
    NOT_SENT: {
        label: 'Não enviado',
        badge: 'badge-ghost',
        icon: MdInsertDriveFile,
    },
    PENDING: {
        label: 'Pendente',
        badge: 'badge-warning',
        icon: MdWarningAmber,
    },
    AWAITING_APPROVAL: {
        label: 'Em análise',
        badge: 'badge-info',
        icon: MdHourglassEmpty,
    },
    APPROVED: {
        label: 'Aprovado',
        badge: 'badge-success',
        icon: MdCheckCircle,
    },
    REJECTED: {
        label: 'Reprovado',
        badge: 'badge-error',
        icon: MdErrorOutline,
    },
}

function statusInfo(status: string) {
    return (
        DOC_STATUS[status?.toUpperCase()] || {
            label: status || 'Desconhecido',
            badge: 'badge-ghost',
            icon: MdInsertDriveFile,
        }
    )
}

export default function DocumentsSection({
    canUpdate,
    onChanged,
}: {
    canUpdate: boolean
    onChanged?: () => void
}) {
    const [docs, setDocs] = useState<AccountDocuments | null>(null)
    const [loading, setLoading] = useState(true)

    const load = async () => {
        setLoading(true)
        try {
            const res = await getAccountDocuments()
            if (res.success && res.result) {
                setDocs(res.result)
            } else {
                toast.error(res.error || 'Erro ao carregar documentos')
            }
        } finally {
            setLoading(false)
        }
    }

    useEffect(() => {
        load()
    }, [])

    const handleAfterChange = () => {
        load()
        onChanged?.()
    }

    if (loading) {
        return (
            <div className="flex justify-center py-12">
                <span className="loading loading-spinner loading-md text-secondary" />
            </div>
        )
    }

    if (!docs || docs.groups.length === 0) {
        return (
            <div className="flex flex-col items-center justify-center py-12 text-base-content/50">
                <MdCheckCircle className="text-5xl mb-2 text-success" />
                <p className="text-sm">
                    Nenhuma pendência de documentação no momento.
                </p>
                <button
                    className="btn btn-xs btn-ghost mt-3"
                    onClick={handleAfterChange}
                >
                    <MdRefresh /> Atualizar
                </button>
            </div>
        )
    }

    return (
        <div className="space-y-4">
            <div className="flex items-center justify-between">
                <p className="text-xs text-base-content/60">
                    Envie os documentos solicitados pelo provedor para liberar a
                    movimentação da conta. Formatos aceitos: PDF, PNG ou JPG.
                </p>
                <button
                    className="btn btn-xs btn-ghost"
                    onClick={handleAfterChange}
                >
                    <MdRefresh /> Atualizar
                </button>
            </div>

            {docs.rejectReasons && (
                <div className="alert alert-error text-sm">
                    <MdErrorOutline className="text-lg" />
                    <span>{docs.rejectReasons}</span>
                </div>
            )}

            {docs.groups.map(group => (
                <DocumentGroupCard
                    key={group.id}
                    group={group}
                    canUpdate={canUpdate}
                    onChanged={handleAfterChange}
                />
            ))}
        </div>
    )
}

function DocumentGroupCard({
    group,
    canUpdate,
    onChanged,
}: {
    group: AccountDocumentGroup
    canUpdate: boolean
    onChanged: () => void
}) {
    const fileInputRef = useRef<HTMLInputElement>(null)
    const [uploading, setUploading] = useState(false)
    const [removingId, setRemovingId] = useState<string | null>(null)
    const [selectedType, setSelectedType] = useState<string>(
        group.documentTypes[0] || group.type || 'IDENTIFICATION'
    )

    const info = statusInfo(group.status)
    const StatusIcon = info.icon

    const handlePick = () => {
        if (!canUpdate) return
        fileInputRef.current?.click()
    }

    const handleFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0]
        // Reset pra permitir reenviar o mesmo arquivo depois.
        e.target.value = ''
        if (!file) return
        setUploading(true)
        try {
            const res = await sendAccountDocument(group.id, selectedType, file)
            if (res.success) {
                toast.success('Documento enviado')
                onChanged()
            } else {
                toast.error(res.error || 'Erro ao enviar documento')
            }
        } finally {
            setUploading(false)
        }
    }

    const handleRemove = async (fileId: string) => {
        if (!confirm('Remover este arquivo enviado?')) return
        setRemovingId(fileId)
        try {
            const res = await deleteAccountDocumentFile(fileId)
            if (res.success) {
                toast.success('Arquivo removido')
                onChanged()
            } else {
                toast.error(res.error || 'Erro ao remover')
            }
        } finally {
            setRemovingId(null)
        }
    }

    return (
        <div className="border border-base-300/60 rounded-2xl p-4 bg-base-100">
            <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                        <h4 className="font-semibold text-sm">
                            {group.title || group.type || 'Documento'}
                        </h4>
                        <span
                            className={`badge badge-sm gap-1 ${info.badge}`}
                        >
                            <StatusIcon className="text-xs" />
                            {info.label}
                        </span>
                    </div>
                    {group.description && (
                        <p className="text-xs text-base-content/60 mt-1">
                            {group.description}
                        </p>
                    )}
                    {group.responsibleName && (
                        <p className="text-xs text-base-content/40 mt-1">
                            Responsável: {group.responsibleName}
                        </p>
                    )}
                </div>
            </div>

            {/* Arquivos já enviados */}
            {group.files.length > 0 && (
                <ul className="mt-3 space-y-2">
                    {group.files.map(f => {
                        const fInfo = statusInfo(f.status || group.status)
                        return (
                            <li
                                key={f.id}
                                className="flex items-center justify-between p-2 rounded-lg bg-base-200/40"
                            >
                                <div className="flex items-center gap-2 min-w-0">
                                    <MdInsertDriveFile className="text-base-content/50 flex-shrink-0" />
                                    <span className="text-xs truncate">
                                        {f.type || 'Arquivo'}
                                    </span>
                                    <span
                                        className={`badge badge-xs ${fInfo.badge}`}
                                    >
                                        {fInfo.label}
                                    </span>
                                </div>
                                {canUpdate && (
                                    <button
                                        className="btn btn-ghost btn-xs btn-circle text-error flex-shrink-0"
                                        onClick={() => handleRemove(f.id)}
                                        disabled={removingId === f.id}
                                        title="Remover arquivo"
                                    >
                                        {removingId === f.id ? (
                                            <span className="loading loading-spinner loading-xs" />
                                        ) : (
                                            <MdDelete />
                                        )}
                                    </button>
                                )}
                            </li>
                        )
                    })}
                </ul>
            )}

            {/* Ações */}
            {group.status !== 'APPROVED' && (
                <div className="mt-3 flex flex-col sm:flex-row sm:items-center gap-2">
                    {group.documentTypes.length > 1 && canUpdate && (
                        <select
                            className="select select-bordered select-sm w-full sm:w-auto"
                            value={selectedType}
                            onChange={e => setSelectedType(e.target.value)}
                            disabled={uploading}
                        >
                            {group.documentTypes.map(t => (
                                <option key={t} value={t}>
                                    {t}
                                </option>
                            ))}
                        </select>
                    )}
                    {canUpdate && (
                        <button
                            className="btn btn-secondary btn-sm"
                            onClick={handlePick}
                            disabled={uploading}
                        >
                            {uploading ? (
                                <span className="loading loading-spinner loading-xs" />
                            ) : (
                                <MdCloudUpload />
                            )}
                            {group.files.length > 0
                                ? 'Enviar outro'
                                : 'Enviar documento'}
                        </button>
                    )}
                    {group.onboardingUrl && (
                        <a
                            href={group.onboardingUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="btn btn-outline btn-sm"
                        >
                            <MdOpenInNew /> Abrir link de onboarding
                        </a>
                    )}
                    <input
                        ref={fileInputRef}
                        type="file"
                        accept="application/pdf,image/png,image/jpeg"
                        className="hidden"
                        onChange={handleFile}
                    />
                </div>
            )}
        </div>
    )
}
